import { ArchivedStepsSection } from "@components/campaigns/ArchivedStepsSection";
import { ConfirmDiffDialog } from "@components/campaigns/ConfirmDiffDialog";
import {
  type CoverageCell,
  type CoverageDimension,
  CoverageMatrix,
  type CoverageStep,
} from "@components/campaigns/CoverageMatrix";
import type { DimensionOption } from "@components/campaigns/StepAssessmentPlanFields";
import { StepCard } from "@components/campaigns/StepCard";
import type { TeamMember } from "@components/campaigns/StepParticipantsEditor";
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  SUGGEST_SELECTION_PROCESS_PLANS,
  UPDATE_SELECTION_PROCESS,
  UPDATE_SELECTION_STEPS,
  UPDATE_STEP_ASSESSMENT_PLAN,
} from "@lib/graphql/mutations";
import { computeDiff, type SelectionStepsDiff } from "@lib/selectionProcessDiff";
import {
  type ArchivedStep,
  type SelectionProcessStep,
  STEP_TYPE_LABELS,
} from "@lib/selectionSteps";
import { ListChecks, Pencil, Plus, Save, X } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { useMutation } from "urql";

interface KeyedStep extends SelectionProcessStep {
  _key: string;
}

interface SelectionProcessEditorProps {
  campaignId: string;
  // When present, enables the Coverage matrix's "Suggest plans for
  // uncovered steps" batch CTA. Null when the campaign has no
  // SelectionProcess record yet (cold start).
  selectionProcessId?: string | null;
  initial: SelectionProcessStep[];
  processDescription?: string | null;
  processVersionNumber?: number | null;
  processVersionOrigin?: string | null;
  locked: boolean;
  teamMembers?: TeamMember[];
  dimensions: DimensionOption[];
  // True when the assessment matrix's bands no longer line up with the
  // current blueprint dimensions. Disables AI-suggestion CTAs that would
  // otherwise fail with the new "out of sync" diagnostic.
  matrixOutOfSync?: boolean;
  coverage: {
    dimensions: CoverageDimension[];
    steps: CoverageStep[];
    cells: CoverageCell[];
  };
  archived?: ArchivedStep[];
  onSaved: () => void;
}

const emptyRow = (): SelectionProcessStep => ({
  stepType: "interview",
  label: null,
  stepFormat: null,
  durationMinutes: null,
  participants: [],
  purpose: null,
});

function stripKey(step: KeyedStep): SelectionProcessStep {
  const { _key, ...rest } = step;
  return rest;
}

function keyFor(prefix: string, n: number): string {
  return `${prefix}-${n}`;
}

export function SelectionProcessEditor({
  campaignId,
  selectionProcessId,
  initial,
  processDescription,
  processVersionNumber,
  processVersionOrigin,
  locked,
  teamMembers = [],
  dimensions,
  matrixOutOfSync = false,
  coverage,
  archived = [],
  onSaved,
}: SelectionProcessEditorProps) {
  const keyPrefix = useId();
  const [editing, setEditing] = useState(false);
  const [nextKey, setNextKey] = useState(initial.length);
  const [rows, setRows] = useState<KeyedStep[]>(
    (initial.length > 0 ? initial : [emptyRow()]).map((s, i) => ({
      ...s,
      _key: keyFor(keyPrefix, i),
    })),
  );
  const [reason, setReason] = useState("");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [description, setDescription] = useState(processDescription ?? "");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [perStepPlanErrors, setPerStepPlanErrors] = useState<Record<string, string>>({});

  const [{ fetching: descSaving }, updateSelectionProcess] = useMutation(UPDATE_SELECTION_PROCESS);
  const [, updateSelectionSteps] = useMutation(UPDATE_SELECTION_STEPS);
  const [, updateStepAssessmentPlan] = useMutation(UPDATE_STEP_ASSESSMENT_PLAN);
  const [{ fetching: batchSuggesting }, suggestProcessPlans] = useMutation(
    SUGGEST_SELECTION_PROCESS_PLANS,
  );
  const [batchError, setBatchError] = useState<string | null>(null);

  // Interview-flavoured steps whose Coverage matrix row is fully empty. We
  // surface a batch CTA when this is non-zero — clicking it fires the
  // coordinator job which fans out GenerateStepAssessmentPlanJob for each.
  const INTERVIEW_FLAVOURED = new Set([
    "interview",
    "recruiter_call",
    "recruiter_interview",
    "final",
  ]);
  const uncoveredInterviewSteps = (coverage.steps ?? []).filter(
    (s) => INTERVIEW_FLAVOURED.has(s.stepType) && s.dimensionsCovered === 0,
  ).length;
  const canBatchSuggest =
    !!selectionProcessId && !matrixOutOfSync && uncoveredInterviewSteps > 0 && !locked;

  const fireBatchSuggest = async () => {
    if (!selectionProcessId) return;
    setBatchError(null);
    const result = await suggestProcessPlans({
      input: { selectionProcessId },
    });
    const data = result.data?.suggestSelectionProcessPlans;
    if (data && Array.isArray(data.errors) && data.errors.length > 0) {
      setBatchError(data.errors[0] as string);
      return;
    }
    if (data?.enqueued) {
      onSaved();
    }
  };

  const batchSuggestCta =
    canBatchSuggest || batchError ? (
      <div
        data-testid="coverage-batch-cta"
        className="flex items-start gap-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800"
      >
        <span className="flex-1">
          {batchError ??
            `${uncoveredInterviewSteps} interview ${uncoveredInterviewSteps === 1 ? "step has" : "steps have"} no assessment plan. Let AI propose intent and coverage for each — you can review and edit before they become yours.`}
        </span>
        {!batchError && (
          <button
            type="button"
            onClick={() => void fireBatchSuggest()}
            disabled={batchSuggesting}
            data-testid="coverage-batch-suggest"
            className="inline-flex items-center gap-1 rounded bg-accent px-2 py-1 text-xs font-medium text-white hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {batchSuggesting ? "Suggesting…" : "Suggest plans for uncovered steps"}
          </button>
        )}
      </div>
    ) : null;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const dimensionNameByContinuity = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of dimensions) map.set(d.continuityId, d.name);
    return map;
  }, [dimensions]);

  const resolveDimensionName = useMemo(
    () => (continuityId: string) => dimensionNameByContinuity.get(continuityId) ?? null,
    [dimensionNameByContinuity],
  );

  const droppedCoverageCount = useMemo(() => {
    if (!confirmOpen) return 0;
    let n = 0;
    for (const r of rows) {
      for (const c of r.assessmentPlan?.coverage ?? []) {
        if (!dimensionNameByContinuity.has(c.dimensionId)) n += 1;
      }
    }
    return n;
  }, [confirmOpen, rows, dimensionNameByContinuity]);

  const pendingDiff = useMemo<SelectionStepsDiff>(
    () =>
      confirmOpen
        ? computeDiff(initial, rows.map(stripKey), resolveDimensionName)
        : { added: [], removed: [], reordered: [], edited: [] },
    [confirmOpen, initial, rows, resolveDimensionName],
  );

  const startEditing = () => {
    const seed = initial.length > 0 ? initial : [emptyRow()];
    setRows(seed.map((s, i) => ({ ...s, _key: keyFor(keyPrefix, i) })));
    setNextKey(seed.length);
    setReason("");
    setExpanded({});
    setPerStepPlanErrors({});
    setEditing(true);
  };

  const handleCancel = () => {
    setRows(initial.map((s, i) => ({ ...s, _key: keyFor(keyPrefix, i) })));
    setReason("");
    setExpanded({});
    setPerStepPlanErrors({});
    setEditing(false);
  };

  const addRow = () => {
    const _key = keyFor(keyPrefix, nextKey);
    setRows([...rows, { ...emptyRow(), _key }]);
    setNextKey((n) => n + 1);
    setExpanded({ ...expanded, [_key]: true });
  };

  const removeRow = (key: string) => setRows(rows.filter((r) => r._key !== key));

  const updateRow = (key: string, next: SelectionProcessStep) =>
    setRows(rows.map((r) => (r._key === key ? { ...next, _key: key } : r)));

  const toggleExpanded = (key: string) => setExpanded({ ...expanded, [key]: !expanded[key] });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = rows.findIndex((r) => r._key === active.id);
    const newIndex = rows.findIndex((r) => r._key === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    setRows(arrayMove(rows, oldIndex, newIndex));
  };

  const handleDescriptionBlur = async () => {
    const trimmed = description.trim();
    const current = (processDescription ?? "").trim();
    if (trimmed === current) return;
    await updateSelectionProcess({
      input: { campaignId, description: trimmed === "" ? null : trimmed },
    });
    onSaved();
  };

  const buildStepsInput = () => {
    const trimmedReason = reason.trim();
    return {
      campaignId,
      steps: rows.map((r) => ({
        ...(r.continuityId ? { continuityId: r.continuityId } : {}),
        stepType: r.stepType,
        label: r.label?.trim() ? r.label.trim() : null,
        ...(r.stepFormat ? { stepFormat: r.stepFormat } : {}),
        ...(r.durationMinutes != null ? { durationMinutes: r.durationMinutes } : {}),
        ...(r.participants
          ? {
              participants: r.participants
                .filter((p) => p.role)
                .map((p) => ({
                  role: p.role,
                  ...(p.name ? { name: p.name } : {}),
                  ...(p.organisationMembershipId
                    ? { organisationMembershipId: p.organisationMembershipId }
                    : {}),
                })),
            }
          : {}),
        ...(r.purpose != null ? { purpose: r.purpose } : {}),
        ...(locked && trimmedReason ? { changeReason: trimmedReason } : {}),
      })),
    };
  };

  const handleSave = () => {
    setPerStepPlanErrors({});
    setConfirmOpen(true);
  };

  const handleConfirmCancel = () => setConfirmOpen(false);

  const handleConfirmApply = async () => {
    setSaving(true);
    setPerStepPlanErrors({});
    try {
      const diff = computeDiff(initial, rows.map(stripKey));

      const isPlanField = (label: string) => label === "Assessment intent" || label === "Coverage";

      const structuralChanged =
        diff.added.length > 0 ||
        diff.removed.length > 0 ||
        diff.reordered.length > 0 ||
        diff.edited.some((e) => e.fieldChanges.some((fc) => !isPlanField(fc.label)));

      const planChangedIds = new Set<string>(
        diff.edited
          .filter((e) => e.fieldChanges.some((fc) => isPlanField(fc.label)))
          .map((e) => e.continuityId),
      );

      // Steps whose persisted coverage still contains entries referencing
      // dimensions no longer in the matrix — these get a plan save too, even
      // if the user didn't edit anything, so the stale entries are actually
      // pruned (otherwise droppedCoverageCount surfaces a phantom count that
      // never goes away).
      const stalePlanIds = new Set<string>(
        rows
          .filter(
            (r) =>
              !!r.continuityId &&
              (r.assessmentPlan?.coverage ?? []).some(
                (c) => !dimensionNameByContinuity.has(c.dimensionId),
              ),
          )
          .map((r) => r.continuityId as string),
      );

      const planRows = rows.filter(
        (r) =>
          !!r.continuityId &&
          !!r.id &&
          (planChangedIds.has(r.continuityId as string) ||
            stalePlanIds.has(r.continuityId as string)),
      );

      if (structuralChanged) {
        const result = await updateSelectionSteps({ input: buildStepsInput() });
        const errors = result.data?.updateSelectionSteps?.errors;
        if (errors && errors.length > 0) {
          // Structural save failed — abort the batch; keep dialog open so the
          // user sees the failure context.
          setSaving(false);
          return;
        }
      }

      if (planRows.length > 0) {
        const settled = await Promise.allSettled(
          planRows.map((row) =>
            updateStepAssessmentPlan({
              input: {
                selectionStepId: row.id as string,
                intent: row.assessmentPlan?.intent ?? null,
                // Prune coverage entries whose continuity is no longer in the
                // current Assessment Matrix — they would fail validation on
                // the server and block the entire save. The diff modal
                // surfaces the prune to the user via `droppedCoverageCount`.
                coverage: (row.assessmentPlan?.coverage ?? [])
                  .filter((c) => dimensionNameByContinuity.has(c.dimensionId))
                  .map((c) => ({ dimensionId: c.dimensionId, depth: c.depth })),
              },
            }).then((result) => ({ row, result })),
          ),
        );

        const nextErrors: Record<string, string> = {};
        for (const outcome of settled) {
          if (outcome.status === "rejected") {
            // urql network/transport error — best-effort generic message.
            // We don't know which row hit it from a rejected promise here.
            continue;
          }
          const { row, result } = outcome.value;
          const errors = result.data?.updateStepAssessmentPlan?.errors as string[] | undefined;
          if (errors && errors.length > 0 && row.continuityId) {
            nextErrors[row.continuityId] = errors.join("; ");
          } else if (result.error && row.continuityId) {
            nextErrors[row.continuityId] = result.error.message;
          }
        }

        if (Object.keys(nextErrors).length > 0) {
          setPerStepPlanErrors(nextErrors);
          setSaving(false);
          setConfirmOpen(false);
          // Stay in edit mode — the user retries by saving again.
          return;
        }
      }

      setSaving(false);
      setConfirmOpen(false);
      setEditing(false);
      onSaved();
    } catch (_e) {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div
        className="space-y-3 rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring"
        data-testid="selection-process-editor"
      >
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ListChecks size={14} className="text-text-secondary" />
            <h3 className="text-sm font-medium text-text-secondary">Selection Process</h3>
          </div>
          <button
            type="button"
            onClick={startEditing}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs text-text-muted hover:bg-card-hover"
          >
            <Pencil size={12} /> Edit
          </button>
        </div>

        <div>
          <div className="mb-1 flex items-center justify-between">
            <label
              htmlFor="selection-process-description"
              className="block text-xs font-medium text-text-secondary"
            >
              Description
            </label>
            {processVersionNumber != null && (
              <span
                className="rounded bg-card-hover px-2 py-0.5 text-xs text-text-faint"
                data-testid="selection-process-version-chip"
              >
                v{processVersionNumber} &middot; {processVersionOrigin ?? "user"}
              </span>
            )}
          </div>
          <textarea
            id="selection-process-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={handleDescriptionBlur}
            disabled={descSaving}
            aria-label="Selection process description"
            placeholder="How this hiring process actually runs"
            rows={2}
            className="w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </div>

        {coverage.dimensions.length > 0 && coverage.steps.length > 0 && (
          <>
            {batchSuggestCta}
            <CoverageMatrix
              dimensions={coverage.dimensions}
              steps={coverage.steps}
              cells={coverage.cells}
            />
          </>
        )}

        {initial.length === 0 ? (
          <p className="text-sm text-text-muted">No selection steps defined yet</p>
        ) : (
          <ol className="space-y-1">
            {initial.map((s, i) => (
              <li
                key={s.id ?? `${s.stepType}-${i}`}
                className="flex flex-wrap items-baseline gap-2 text-sm"
              >
                <span className="text-text-faint">{i + 1}.</span>
                <span className="text-text-primary">
                  {s.label?.trim() || (STEP_TYPE_LABELS[s.stepType] ?? s.stepType)}
                </span>
              </li>
            ))}
          </ol>
        )}

        <ArchivedStepsSection campaignId={campaignId} archived={archived} onSaved={onSaved} />
      </div>
    );
  }

  return (
    <div
      className="space-y-3 rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring"
      data-testid="selection-process-editor"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ListChecks size={14} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">Selection Process</h3>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleCancel}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs text-text-muted hover:bg-card-hover"
          >
            <X size={12} /> Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            data-testid="selection-process-review-save"
            className="flex items-center gap-1 rounded bg-accent px-3 py-1 text-xs font-medium text-white hover:bg-accent/90 disabled:opacity-50"
          >
            <Save size={12} /> {saving ? "Saving..." : "Review changes & save"}
          </button>
        </div>
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <label
            htmlFor="selection-process-description"
            className="block text-xs font-medium text-text-secondary"
          >
            Description
          </label>
          {processVersionNumber != null && (
            <span
              className="rounded bg-card-hover px-2 py-0.5 text-xs text-text-faint"
              data-testid="selection-process-version-chip"
            >
              v{processVersionNumber} &middot; {processVersionOrigin ?? "user"}
            </span>
          )}
        </div>
        <textarea
          id="selection-process-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onBlur={handleDescriptionBlur}
          disabled={descSaving}
          aria-label="Selection process description"
          placeholder="How this hiring process actually runs"
          rows={2}
          className="w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
        />
      </div>

      {coverage.dimensions.length > 0 && coverage.steps.length > 0 && (
        <>
          {batchSuggestCta}
          <CoverageMatrix
            dimensions={coverage.dimensions}
            steps={coverage.steps}
            cells={coverage.cells}
          />
        </>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={rows.map((r) => r._key)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {rows.map((row, i) => (
              <StepCard
                key={row._key}
                id={row._key}
                step={row}
                index={i}
                expanded={!!expanded[row._key]}
                onToggleExpanded={() => toggleExpanded(row._key)}
                onChange={(next) => updateRow(row._key, next)}
                onRemove={() => removeRow(row._key)}
                locked={locked}
                teamMembers={teamMembers}
                dimensions={dimensions}
                coverageCells={coverage.cells}
                matrixOutOfSync={matrixOutOfSync}
                saveError={row.continuityId ? (perStepPlanErrors[row.continuityId] ?? null) : null}
                onPlanSaved={onSaved}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <button
        type="button"
        onClick={addRow}
        className="flex items-center gap-1 text-xs text-accent hover:text-accent/80"
      >
        <Plus size={12} /> Add step
      </button>

      <ArchivedStepsSection campaignId={campaignId} archived={archived} onSaved={onSaved} />

      {locked && (
        <div className="border-t border-border pt-3">
          <label
            htmlFor="selection-step-reason"
            className="mb-1 block text-xs font-medium text-text-secondary"
          >
            Reason for change
          </label>
          <input
            id="selection-step-reason"
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            aria-label="Reason for change"
            placeholder="The brief is locked — note why the process is changing"
            className="w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
      )}

      <ConfirmDiffDialog
        open={confirmOpen}
        diff={pendingDiff}
        droppedCoverageCount={droppedCoverageCount}
        onCancel={handleConfirmCancel}
        onConfirm={handleConfirmApply}
        busy={saving}
      />
    </div>
  );
}
